import React, { useState } from 'react'
import { StyleSheet } from 'react-native'
import {
  DNAIcon,
  Iffy,
  DNABox,
  DNAText,
  DNAModal,
  DNACard,
  DNAButton,
  util,
} from '@alucio/lux-ui'

import { DNAModalVariant } from './DNAConnectedModal'
import { HubORM } from 'src/types/orms'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import HubsTemplateSelectEmpty from '../Empty/HubsTemplateSelectEmpty'
import { useCurrentUser } from 'src/state/redux/selector/user'

const S = StyleSheet.create({
  templateBox: {
    padding: 12,
    flex: 1,
    alignItems: 'center',
  },
  templateCardSelected: {
    borderColor: colors['color-brand2-500'],
    borderWidth: 2,
    borderRadius: 8,
    backgroundColor: colors['color-brand2-5'],
  },
  templateCard: {
    maxHeight: 64,
    minHeight: 64, // not sure why height: 64 is not working
    height: 64,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: colors['color-gray-100'],
    borderRadius: 8,
  },
  templateList: {
    overflow: 'scroll',
    height: 440,
    width: 600,
  },
  templateListContainer: {
    height: 440,
    width: 600,
  },
});

const DNASelectTemplateModal: DNAModalVariant<{
  hubTemplatesORM: HubORM[]
  handleCreateTemplateInstance: (template: HubORM) => void
  handleCreateHubs: () => void
}> = (props) => {
  const currentUser = useCurrentUser()
  const { toggleModal, hubTemplatesORM, handleCreateTemplateInstance, handleCreateHubs } = props
  // the reason we are checking current user here instead of the selector is
  // in the publishers dashboard we show the templates regardless of the owner status but in the viewer we need to show based on owner
  const templates = hubTemplatesORM.filter(hubORM =>
    hubORM.relations.sharedPermissions.some(permission => permission.meta.isValidPermission) ||
    hubORM.model.createdBy === currentUser?.userProfile?.id,
  )
  const sortedTemplates = templates.sort((a, b) => a.model?.name?.localeCompare(b.model?.name || '') || 0)
  const [selectedTemplate, setSelectedTemplate] = useState<HubORM>(sortedTemplates[0])

  const handleSelectTemplate = (template: HubORM) => {
    setSelectedTemplate(template)
  }

  const createTemplateInstance = () => {
    handleCreateTemplateInstance(selectedTemplate)
    toggleModal()
  }

  return (
    <DNAModal>
      <DNAModal.Header onClose={toggleModal}>
        Select template
      </DNAModal.Header>
      <DNAModal.Body>
        <Iffy is={sortedTemplates.length === 0}>
          <DNABox fill alignX="center" alignY="center" style={S.templateListContainer}>
            <HubsTemplateSelectEmpty handleCreateHubs={handleCreateHubs} />
          </DNABox>
        </Iffy>
        <Iffy is={sortedTemplates.length > 0}>
          <DNABox appearance="col" style={S.templateList} fill>
            {sortedTemplates.map(template => (
              <DNACard
                key={template.model.id}
                appearance="flat"
                interactive="pressable"
                onPress={() => handleSelectTemplate(template)}
                style={util.mergeStyles(
                  undefined,
                  S.templateCard,
                  [S.templateCardSelected, selectedTemplate?.model.id === template.model.id],
                )}
              >
                <DNABox
                  appearance="row"
                  spacing="md"
                  style={S.templateBox}
                  testID={`hub-template-${template.model.id}`}
                  childFill={1}
                >
                  <DNAIcon.Styled name="layout"/>
                  <DNAText numberOfLines={1}>{template.model.name}</DNAText>
                </DNABox>
              </DNACard>
            ))}
          </DNABox>
        </Iffy>
      </DNAModal.Body>
      <Iffy is={sortedTemplates.length > 0}>
        <DNAModal.Footer>
          <DNABox spacing="sm" fill alignX="end">
            <DNAButton status="tertiary" appearance="outline" onPress={toggleModal}>Cancel</DNAButton>
            <DNAButton status="primary" onPress={() => createTemplateInstance()}>Create</DNAButton>
          </DNABox>
        </DNAModal.Footer>
      </Iffy>
    </DNAModal>
  )
}

export default DNASelectTemplateModal
